import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { movies } from '@/mocks/movies';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '分类', to: '/category' },
  { label: '专题', to: '/special' },
  { label: '关于我们', to: '/about' },
];

interface SiteHeaderProps {
  transparentTop?: boolean;
}

export default function SiteHeader({ transparentTop = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const solid = scrolled || !transparentTop;

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    setSuggestOpen(false);
    setMenuOpen(false);
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const suggestions = keyword.trim()
    ? movies.filter((m) => m.title.includes(keyword.trim())).slice(0, 5)
    : [];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-gradient-to-b from-black/70 via-black/30 to-transparent border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-20 flex items-center gap-4 md:gap-8">
        <Link to="/" className="flex items-center gap-2 shrink-0" onClick={() => setMenuOpen(false)}>
          <span className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-primary-500 flex items-center justify-center">
            <span className="font-heading text-foreground-950 text-lg md:text-xl font-bold">禁</span>
          </span>
          <span className="leading-none">
            <span className="block font-heading text-foreground-950 text-lg md:text-xl font-bold tracking-wide">
              十八禁
            </span>
            <span className="block font-label text-[10px] md:text-[11px] text-foreground-500 tracking-[0.3em]">
              FILM PORTAL
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-2">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="px-3.5 py-2 rounded-md text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:block ml-auto relative">
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onFocus={() => setSuggestOpen(true)}
                onBlur={() => window.setTimeout(() => setSuggestOpen(false), 150)}
                placeholder="搜索影片、剧集或关键词"
                className="w-56 lg:w-72 bg-background-100 border border-background-200 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm rounded-md px-4 py-2 whitespace-nowrap cursor-pointer transition-colors"
            >
              搜索
            </button>
          </form>
          {suggestOpen && suggestions.length > 0 && (
            <div className="absolute right-0 mt-2 w-72 bg-background-100 border border-background-200 rounded-md overflow-hidden">
              {suggestions.map((m) => (
                <Link
                  key={m.id}
                  to={`/detail/${m.id}`}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-background-200 transition-colors"
                >
                  <span className="w-8 h-11 rounded-sm overflow-hidden bg-background-300 shrink-0">
                    <img src={m.poster} alt={m.title} className="w-full h-full object-top object-cover" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm text-foreground-900 truncate">{m.title}</span>
                    <span className="block text-xs text-foreground-500">
                      {m.year} · {m.type === 'movie' ? '电影' : '电视剧'}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          aria-label="打开导航菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md bg-background-100/80 text-foreground-900 cursor-pointer"
        >
          <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/70 px-4 py-4 animate-fade-in">
          <form onSubmit={submitSearch} className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、剧集或关键词"
                className="w-full bg-background-100 border border-background-200 rounded-md pl-9 pr-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="bg-primary-500 text-foreground-950 text-sm rounded-md px-4 py-2.5 whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className="px-4 py-3 rounded-md bg-background-100 text-sm text-foreground-800 hover:text-foreground-950 transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}